import { useMemo, useState } from "react";
import { ComponentsHelper } from "../../../core/services/ComponentHelper";
import {
	ChevronDown16Regular,
	ChevronLeft20Regular,
	ChevronRight20Regular,
	ChevronUp16Regular,
} from "@fluentui/react-icons";
import IconTextButton from "../../../repertoire/components/iconTextButton/IconTextButton";
import { IRepertoireField } from "../../../repertoire/types/IRepertoireField";
import { parseDateType } from "../../../membersportal/util/dateHelpers";
import { RESULTS, OF } from "../../../membersportal/Consts";

interface IReadOnlyDivTableProps {
	fields: IRepertoireField[];
	dataSource: any[];
	titleFieldName?: string;
	emptyText?: string;
	customerDatePreference?: string;
	pagination?: boolean;
	pageSize?: number;
	viewData?: Record<string, string>;
}

const ReadOnlyDivHeader = ({
	title,
	isOpen,
	onToggle,
}: {
	title: React.ReactNode;
	isOpen: boolean;
	onToggle: () => void;
}) => {
	const ToggleIcon = isOpen
		? ChevronUp16Regular
		: ChevronDown16Regular;

	return (
		<div
			className="dataDivHeader"
			onClick={onToggle}
			onKeyDown={(event) => {
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					onToggle();
				}
			}}
			role="button"
			tabIndex={0}
			aria-expanded={isOpen}
		>
			<div className="dataDivInfoHeader">{title}</div>

			<div>
				<span style={{ pointerEvents: "none" }}>
					<IconTextButton icon={ToggleIcon} disabled={false} />
				</span>
			</div>
		</div>
	);
};

const ReadOnlyDivContent = ({
	row,
	fields,
}: {
	row: any;
	fields: IRepertoireField[];
}) => (
	<div className="dataDivContent borderTop">
		{fields.map((field) => (
			<div className="dataDivContentRow" key={field.name}>
				<span className="dataDivLabel">{field.data}:</span>
				<span className="dataDivValue">{row[field.name]}</span>
			</div>
		))}
	</div>
);

export const ReadOnlyDivTable = ({
	fields,
	dataSource,
	titleFieldName,
	emptyText,
	customerDatePreference,
	pagination = false,
	pageSize = 5,
	viewData,
}: IReadOnlyDivTableProps) => {
	const [openRows, setOpenRows] = useState<Record<string, boolean>>({});
	const [currentPage, setCurrentPage] = useState(1);
	const [pageInput, setPageInput] = useState("1");

	const orderedFields = useMemo(() => {
		const ordered = ComponentsHelper.orderFieldsBySpecificField(
			fields,
			"order"
		);
		return ordered?.length ? ordered : fields;
	}, [fields]);

	const contentFields = useMemo(
		() => orderedFields.filter((field) => field.name !== titleFieldName),
		[orderedFields, titleFieldName]
	);

	const toggleRow = (key: string) => {
		setOpenRows((prev) => ({
			...prev,
			[key]: !prev[key],
		}));
	};

	const data = useMemo(() => {
		return dataSource?.map((row) => ({
			key: row?.applicationId ?? row?.ticketNumber,
			applicationId: row?.applicationId,
			ticketNumber: row?.ticketNumber,
			title: row?.title,
			caseType: row?.caseType,
			caseOrigin: row?.caseOrigin,
			contact: row?.name,
			submittedDateTime:
				row?.submittedDateTime &&
				parseDateType(
					row?.submittedDateTime,
					customerDatePreference + " HH:mm:ss",
				),
			lastModifiedDateTime:
				row?.lastModifiedDate &&
				parseDateType(
					row?.lastModifiedDate,
					customerDatePreference + " HH:mm:ss",
				),
			status: row?.status,
		}));
	}, [dataSource]);

	const totalPages = Math.max(1, Math.ceil((data?.length || 0) / pageSize));

	const visibleData = useMemo(() => {
		if (!pagination) {
			return data;
		}

		const startIndex = (currentPage - 1) * pageSize;
		return data?.slice(startIndex, startIndex + pageSize);
	}, [currentPage, data, pageSize, pagination]);

	const changePage = (page: number) => {
		if (page < 1 || page > totalPages || page === currentPage) {
			return;
		}

		setOpenRows({});
		setCurrentPage(page);
		setPageInput(page.toString());
	};

	const commitPageInput = () => {
		const page = Number(pageInput);

		if (!Number.isInteger(page)) {
			setPageInput(currentPage.toString());
			return;
		}

		const validPage = Math.min(Math.max(page, 1), totalPages);
		setPageInput(validPage.toString());
		changePage(validPage);
	};

	return (
		<div className="dataDivTable">
			{data?.length > 0 ? (
				<>
					{visibleData.map((row, index) => {
						const key = row.key ?? `${index}`;
						const isOpen = !!openRows[key];

						return (
							<div
								className={`dataDivRow ${!isOpen ? "borderBottom" : ""}`}
								key={key}
							>
								<ReadOnlyDivHeader
									title={row[titleFieldName]}
									isOpen={isOpen}
									onToggle={() => toggleRow(key)}
								/>

								{isOpen && (
									<ReadOnlyDivContent
										row={row}
										fields={contentFields}
									/>
								)}
							</div>
						);
					})}

					{pagination && totalPages > 1 && (
						<div className="dataDivPagination">
							<div className="dataDivPaginationResults">
								{data.length} {viewData?.[RESULTS] || "Results"}
							</div>

							<div className="dataDivPaginationControls">
								<button
									type="button"
									className="dataDivPaginationButton"
									onClick={() => changePage(currentPage - 1)}
									disabled={currentPage === 1}
									aria-label="Previous page"
								>
									<ChevronLeft20Regular />
								</button>

								<input
									type="text"
									className="dataDivPaginationCurrent"
									value={pageInput}
									inputMode="numeric"
									pattern="[0-9]*"
									onChange={(event) => setPageInput(event.target.value)}
									onBlur={commitPageInput}
									onKeyDown={(event) => {
										if (event.key === "Enter") {
											event.currentTarget.blur();
										}
									}}
									aria-label="Page number"
								/>

								<span className="dataDivPaginationTotal">
									{viewData?.[OF] || "of"} {totalPages}
								</span>

								<button
									type="button"
									className="dataDivPaginationButton"
									onClick={() => changePage(currentPage + 1)}
									disabled={currentPage === totalPages}
									aria-label="Next page"
								>
									<ChevronRight20Regular />
								</button>
							</div>
						</div>
					)}
				</>
			) : (
				<div className="noData">{emptyText}</div>
			)}
		</div>
	);
};